/**
 * Temperature formatting utilities
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { FieldValue, FormattedValue, TemperatureFormat, TemperatureUnit, TemperatureUnitString } from "./types";

/**
 * Convert temperature unit string to symbol
 */
export const temperatureStringToSymbol = (unit: TemperatureUnitString): TemperatureUnit => {
  switch (unit) {
    case "FAHRENHEIT":
      return "F";
    case "CELSIUS":
      return "C";
    case "KELVIN":
      return "K";
    default:
      return "F";
  }
};

/**
 * Format as Fahrenheit
 */
export const toFahrenheit = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)}°F`;
};

/**
 * Format as Celsius
 */
export const toCelsius = (
  value: number | null | undefined,
  fromFahrenheit: boolean = true,
  decimals: number = 0
): string | null => {
  if (isNil(value)) return null;
  const celsius = fromFahrenheit ? (value - 32) * (5 / 9) : value;
  return `${celsius.toFixed(decimals)}°C`;
};

/**
 * Format as Kelvin
 */
export const toKelvin = (
  value: number | null | undefined,
  fromFahrenheit: boolean = true,
  decimals: number = 0
): string | null => {
  if (isNil(value)) return null;
  const kelvin = fromFahrenheit ? (value - 32) * (5 / 9) + 273.15 : value + 273.15;
  return `${kelvin.toFixed(decimals)}K`;
};

/**
 * Convert Celsius to Fahrenheit
 */
export const celsiusToFahrenheit = (celsius: number): number => {
  return (celsius * 9) / 5 + 32;
};

/**
 * Convert Fahrenheit to Celsius
 */
export const fahrenheitToCelsius = (fahrenheit: number): number => {
  return (fahrenheit - 32) * (5 / 9);
};

/**
 * Convert Celsius to Kelvin
 */
export const celsiusToKelvin = (celsius: number): number => {
  return celsius + 273.15;
};

/**
 * Convert Kelvin to Celsius
 */
export const kelvinToCelsius = (kelvin: number): number => {
  return kelvin - 273.15;
};

/**
 * Convert Fahrenheit to Kelvin
 */
export const fahrenheitToKelvin = (fahrenheit: number): number => {
  return fahrenheitToCelsius(fahrenheit) + 273.15;
};

/**
 * Convert Kelvin to Fahrenheit
 */
export const kelvinToFahrenheit = (kelvin: number): number => {
  return celsiusToFahrenheit(kelvinToCelsius(kelvin));
};

/**
 * Format temperature with specified unit
 */
export const toTemperature = (
  value: number | null | undefined,
  unit: TemperatureUnit | TemperatureUnitString = "F",
  decimals: number = 0
): string | null => {
  if (isNil(value)) return null;

  const normalizedUnit =
    typeof unit === "string" && unit.length > 1
      ? temperatureStringToSymbol(unit as TemperatureUnitString)
      : (unit as TemperatureUnit);

  switch (normalizedUnit) {
    case "C":
      return toCelsius(value, true, decimals);
    case "K":
      return toKelvin(value, true, decimals);
    case "F":
    default:
      return toFahrenheit(value, decimals);
  }
};

/**
 * Format temperature according to TemperatureFormat specification
 */
export const formatTemperature = (value: FieldValue, format: TemperatureFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals ?? 0;
  const unit = format.unit ?? "F";

  if (format.showUnit === false) {
    const normalizedUnit =
      typeof unit === "string" && unit.length > 1
        ? temperatureStringToSymbol(unit as TemperatureUnitString)
        : (unit as TemperatureUnit);

    switch (normalizedUnit) {
      case "C": {
        const celsius = (numValue - 32) * (5 / 9);
        return celsius.toFixed(decimals);
      }
      case "K": {
        const kelvin = (numValue - 32) * (5 / 9) + 273.15;
        return kelvin.toFixed(decimals);
      }
      case "F":
      default:
        return numValue.toFixed(decimals);
    }
  }

  return toTemperature(numValue, unit, decimals);
};

// Register temperature formatters
FormatRegistry.register("temperature", (value, unit, decimals) => {
  if (typeof value === "number") {
    return toTemperature(
      value,
      typeof unit === "string" ? (unit as TemperatureUnit | TemperatureUnitString) : "F",
      typeof decimals === "number" ? decimals : 0
    );
  }
  return null;
});

FormatRegistry.register("fahrenheit", (value, decimals) => {
  if (typeof value === "number") {
    return toFahrenheit(value, typeof decimals === "number" ? decimals : 0);
  }
  return null;
});

FormatRegistry.register("celsius", (value, fromFahrenheit, decimals) => {
  if (typeof value === "number") {
    return toCelsius(
      value,
      typeof fromFahrenheit === "boolean" ? fromFahrenheit : true,
      typeof decimals === "number" ? decimals : 0
    );
  }
  return null;
});

FormatRegistry.register("kelvin", (value, fromFahrenheit, decimals) => {
  if (typeof value === "number") {
    return toKelvin(
      value,
      typeof fromFahrenheit === "boolean" ? fromFahrenheit : true,
      typeof decimals === "number" ? decimals : 0
    );
  }
  return null;
});
